/**
 * YIN Algorithm for pitch detection
 * Reference: "YIN, a fundamental frequency estimator for speech and music" by Cheveigné & Kawahara
 */

export interface PitchResult {
  frequency: number;
  confidence: number;
}

/**
 * Detect pitch using YIN algorithm
 */
export function detectPitchYIN(
  buffer: Float32Array,
  sampleRate: number,
  threshold = 0.15
): PitchResult {
  const SIZE = buffer.length;
  const halfSize = Math.floor(SIZE / 2);
  const yinBuffer = new Float32Array(halfSize);

  // Step 1: Calculate difference function
  calculateDifference(buffer, yinBuffer);

  // Step 2: Cumulative mean normalized difference
  cumulativeMeanNormalizedDifference(yinBuffer);

  // Step 3: Absolute threshold
  const tau = absoluteThreshold(yinBuffer, threshold);

  if (tau === -1) {
    return { frequency: 0, confidence: 0 };
  }

  // Step 4: Parabolic interpolation
  const betterTau = parabolicInterpolation(yinBuffer, tau);

  // Calculate frequency from period
  const frequency = sampleRate / betterTau;

  // Confidence is inverse of the YIN value (lower YIN value = higher confidence)
  const yinValue = yinBuffer[tau];
  const confidence = yinValue !== undefined ? 1 - yinValue : 0;

  // Filter out unrealistic frequencies
  if (frequency < 50 || frequency > 1200 || confidence < 0.5) {
    return { frequency: 0, confidence: 0 };
  }

  return { frequency, confidence };
}

function calculateDifference(buffer: Float32Array, yinBuffer: Float32Array): void {
  const halfSize = yinBuffer.length;

  for (let tau = 0; tau < halfSize; tau++) {
    let sum = 0;
    for (let i = 0; i < halfSize; i++) {
      const bufI = buffer[i] ?? 0;
      const bufITau = buffer[i + tau] ?? 0;
      const delta = bufI - bufITau;
      sum += delta * delta;
    }
    yinBuffer[tau] = sum;
  }
}

function cumulativeMeanNormalizedDifference(yinBuffer: Float32Array): void {
  let runningSum = 0;

  yinBuffer[0] = 1;

  for (let tau = 1; tau < yinBuffer.length; tau++) {
    const val = yinBuffer[tau] ?? 0;
    runningSum += val;
    yinBuffer[tau] = val * tau / runningSum;
  }
}

function absoluteThreshold(yinBuffer: Float32Array, threshold: number): number {
  const startTau = 2;
  const maxTau = yinBuffer.length - 1;

  for (let tau = startTau; tau < maxTau; tau++) {
    const currentVal = yinBuffer[tau] ?? Infinity;
    if (currentVal < threshold) {
      let nextVal = yinBuffer[tau + 1] ?? Infinity;
      while (tau + 1 < maxTau && nextVal < currentVal) {
        tau++;
        nextVal = yinBuffer[tau + 1] ?? Infinity;
      }
      return tau;
    }
  }

  let bestTau = startTau;
  let minValue = yinBuffer[startTau] ?? Infinity;
  for (let tau = startTau + 1; tau < maxTau; tau++) {
    const val = yinBuffer[tau] ?? Infinity;
    if (val < minValue) {
      minValue = val;
      bestTau = tau;
    }
  }

  if (minValue < threshold * 2) {
    return bestTau;
  }

  return -1;
}

function parabolicInterpolation(yinBuffer: Float32Array, tau: number): number {
  if (tau === 0 || tau === yinBuffer.length - 1) {
    return tau;
  }

  const s0 = yinBuffer[tau - 1] ?? 0;
  const s1 = yinBuffer[tau] ?? 0;
  const s2 = yinBuffer[tau + 1] ?? 0;

  const denominator = 2 * (2 * s1 - s2 - s0);
  if (denominator === 0) return tau;

  const adjustment = (s2 - s0) / denominator;

  return tau + adjustment;
}
